import { FieldProvenanceDto, MissionAssessmentEffectSelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AreaBarChartComponent } from '@components/shared/area-bar-chart.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { EffectDefinitionTooltip } from '@components/shared/effect-definition.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { formatCurrency } from '@utils/number.util';
import { formatProper } from '@utils/text.util';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { FunctionComponent, useState } from 'react';

type TechnologyMissionAssessmentEffectCardProps = {
  effect: MissionAssessmentEffectSelectDto;
  isPrimary: boolean;
  provenance?: FieldProvenanceDto | null;
  useTooltip?: React.ReactNode;
};

const PrimaryTag: FunctionComponent<{ text: string }> = ({ text }) => (
  <div className="text-sm px-2 text-[#35B39C] bg-[#00BD791A] rounded-sm font-light">{text}</div>
);

export const TechnologyMissionAssessmentEffectCardComponent: FunctionComponent<
  TechnologyMissionAssessmentEffectCardProps
> = (props) => {
  const { effect, isPrimary, provenance, useTooltip } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const tableValues = [
    { label: 'Unit', value: effect.data?.unit ?? '' },
    { label: 'Cost', value: effect.data?.cost ? formatCurrency(effect.data.cost.amount) : '' },
    {
      label: 'Cost to Replace',
      value: effect.data?.costToReplace ? formatCurrency(effect.data.costToReplace.amount) : '',
    },
    {
      label: 'Time to Deploy',
      value: effect.data?.timeToDeploy
        ? `${effect.data.timeToDeploy.value} ${formatProper(effect.data.timeToDeploy.unit)}`
        : '',
    },
    {
      label: 'Time to Replace',
      value: effect.data?.timeToReplace
        ? `${effect.data.timeToReplace.value} ${formatProper(effect.data.timeToReplace.unit)}`
        : '',
    },
    { label: 'Number of Personnel Required', value: effect.data?.personnelRequired?.toString() ?? '' },
  ];

  const sections = [
    {
      description: effect.data?.description ?? '',
      title: 'Characteristics & Measures',
    },
    {
      description: effect.data?.measuresOfPerformance ?? '',
      title: 'Measures of Performance',
    },
    {
      description: effect.data?.measureOfImpact ?? '',
      title: 'Measures of Impact/Effectiveness',
    },
  ];

  const itemValueRange =
    effect.data?.minPhase !== undefined && effect.data?.maxPhase !== undefined
      ? {
          // normalize order and clamp to chart domain [0,5]
          min: Math.max(0, Math.min(5, Math.min(effect.data.minPhase, effect.data.maxPhase))),
          max: Math.max(0, Math.min(5, Math.max(effect.data.minPhase, effect.data.maxPhase))),
        }
      : undefined;

  return (
    <div className="bg-[#fafafa] rounded-lg mt-3">
      {/* Header - collapsed view */}
      <div className="p-4">
        <div className="flex flex-row">
          <div className="flex-1 grid grid-cols-3 items-start gap-4">
            {/* Effect */}
            <div className="flex w-full">
              <TextWithLabel
                title={effect.effect.name}
                label="Effect"
                badge={<EffectDefinitionTooltip definition={effect.effect.description} />}
                className="text-sm font-medium"
              />
            </div>

            {/* Use */}
            <div className="flex w-full">
              <TextWithLabel
                title={effect.use.name}
                label="Use"
                badge={<>{useTooltip && <TooltipComponent side="right">{useTooltip}</TooltipComponent>}</>}
                className="text-sm"
              />
            </div>

            {/* Domain */}
            <div className="flex w-full">
              <TextWithLabel
                title={effect.domain.name}
                label="Domain"
                className="text-sm"
              />
            </div>
          </div>

          {/* Provenance + Expand/Collapse */}
          <div className="ml-auto flex items-center gap-2 mt-1">
            <FieldProvenanceBadge provenance={provenance} />
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[#1F1C1AB2] hover:text-[#1F1C1A] transition-colors"
            >
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <div className="bg-[#C0C0C033] rounded-sm shadow-[0px_4px_12px_0px_#0000000A] p-3 space-y-3">
            {/* Basic information table */}
            <section className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {tableValues.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col gap-1"
                >
                  <div className="text-sm text-[#1F1C1A]">{item.label}</div>
                  <div className="text-sm text-[#1F1C1AB2]">{item.value || '-'}</div>
                </div>
              ))}
            </section>

            {/* Description sections */}
            {sections.map((section) => (
              <div
                key={section.title}
                className="mt-3"
              >
                <div className="text-sm text-[#1F1C1A]">{section.title}</div>
                <div className="text-sm text-[#1F1C1AB2] mt-1">{section.description || '-'}</div>
              </div>
            ))}

            {/* Secondary Effects */}
            {effect.secondaryEffects && effect.secondaryEffects.length > 0 && (
              <div className="mt-3">
                <div className="text-sm text-[#1F1C1A] mb-2">Secondary Effects</div>
                <BadgeList
                  items={effect.secondaryEffects.map((secondaryEffect) => ({
                    ...secondaryEffect,
                    tooltip: secondaryEffect.description,
                  }))}
                  limit={4}
                  badgeVariant="primary"
                  tooltipMode="badge"
                />
              </div>
            )}

            {/* Target Systems */}
            {effect.targetSystems && effect.targetSystems.length > 0 && (
              <div className="mt-3">
                <div className="text-sm text-[#1F1C1A] mb-2">Target Systems</div>
                <BadgeList
                  items={effect.targetSystems}
                  limit={4}
                  badgeVariant="primary"
                />
              </div>
            )}

            {/* Phases of Operation chart */}
            {itemValueRange && effect.data?.minPhase !== undefined && effect.data?.maxPhase !== undefined && (
              <div className="mt-3">
                <div className="text-sm text-[#1F1C1A] mb-2">Phases of Operation</div>
                <AreaBarChartComponent
                  customLabels={[
                    'Shape',
                    'Deter',
                    'Seize Initiative',
                    'Dominate',
                    'Stabilize',
                    'Enable Civil Authority',
                  ]}
                  items={[{ valueRange: itemValueRange }]}
                  valueRange={{ max: 5, min: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
